/**
 * Hangman Statistics Service
 *
 * Records game results via StatisticsManager, tracks per-difficulty
 * stats including win streaks and guess accuracy.
 *
 * Follows SudokuStatisticsService pattern.
 */

import {
  HangmanStatistics,
  HangmanGameResult,
  HangmanDifficultyStats,
  HangmanDifficulty,
} from '../types';
import { StatisticsManager } from '../../services/StatisticsManager';

export class HangmanStatisticsService {
  private statsManager: StatisticsManager;
  private readonly STATS_KEY = 'hangman_statistics';

  constructor(statsManager: StatisticsManager) {
    this.statsManager = statsManager;
  }

  private createEmptyStats(): HangmanStatistics {
    return {
      easy: this.createEmptyDifficultyStats(),
      medium: this.createEmptyDifficultyStats(),
      hard: this.createEmptyDifficultyStats(),
      totalGames: 0,
      totalPlayTime: 0,
      currentStreak: 0,
      bestStreak: 0,
    };
  }

  private createEmptyDifficultyStats(): HangmanDifficultyStats {
    return {
      gamesPlayed: 0,
      gamesWon: 0,
      gamesLost: 0,
      totalGuesses: 0,
      totalWrongGuesses: 0,
      hintsUsed: 0,
      winPercentage: 0,
      currentStreak: 0,
      bestStreak: 0,
    };
  }

  /** Get statistics for a user (or global if no userId). */
  getStatistics(userId?: string): HangmanStatistics {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      const data = localStorage.getItem(key);
      return data ? (JSON.parse(data) as HangmanStatistics) : this.createEmptyStats();
    } catch (error) {
      console.error('Failed to get hangman statistics:', error);
      return this.createEmptyStats();
    }
  }

  /** Record a completed game result. */
  recordGameResult(result: HangmanGameResult, userId?: string): boolean {
    try {
      const stats = this.getStatistics(userId);
      const diffStats = stats[result.difficulty];

      diffStats.gamesPlayed++;
      diffStats.totalGuesses += result.guessCount;
      diffStats.totalWrongGuesses += result.wrongGuessCount;

      if (result.hintUsed) {
        diffStats.hintsUsed++;
      }

      if (result.won) {
        diffStats.gamesWon++;
        diffStats.currentStreak++;
        stats.currentStreak++;

        if (diffStats.currentStreak > diffStats.bestStreak) {
          diffStats.bestStreak = diffStats.currentStreak;
        }
        if (stats.currentStreak > stats.bestStreak) {
          stats.bestStreak = stats.currentStreak;
        }
      } else {
        diffStats.gamesLost++;
        diffStats.currentStreak = 0;
        stats.currentStreak = 0;
      }

      diffStats.winPercentage =
        diffStats.gamesPlayed > 0
          ? (diffStats.gamesWon / diffStats.gamesPlayed) * 100
          : 0;

      stats.totalGames++;
      stats.totalPlayTime += result.time;

      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(stats));
      return true;
    } catch (error) {
      console.error('Failed to record hangman game result:', error);
      return false;
    }
  }

  /** Get statistics for a specific difficulty. */
  getDifficultyStatistics(
    difficulty: HangmanDifficulty,
    userId?: string
  ): HangmanDifficultyStats {
    const stats = this.getStatistics(userId);
    return stats[difficulty];
  }

  /** Reset all statistics. */
  resetStatistics(userId?: string): boolean {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(this.createEmptyStats()));
      return true;
    } catch (error) {
      console.error('Failed to reset hangman statistics:', error);
      return false;
    }
  }
}
